import type { IRExpression, IRPattern } from '../types/ir';

export type TextSegment = { kind: 'text'; value: string } | { kind: 'expr'; expr: IRExpression };

// Flatten a text expression (a left-leaning chain of `+` binops over text and
// non-text operands) into a list of literal-text and computed-expression
// segments. Adjacent text literals are merged.
export function flattenTextExpression(expr: IRExpression): TextSegment[] {
  const segments: TextSegment[] = [];
  collect(expr, segments);
  return mergeAdjacentText(segments);
}

function collect(expr: IRExpression, out: TextSegment[]) {
  if (expr.kind === 'binop' && expr.op === '+') {
    collect(expr.left, out);
    collect(expr.right, out);
    return;
  }
  if (expr.kind === 'literal' && expr.type === 'text') {
    out.push({ kind: 'text', value: expr.value as string });
    return;
  }
  out.push({ kind: 'expr', expr });
}

function mergeAdjacentText(segments: TextSegment[]): TextSegment[] {
  const merged: TextSegment[] = [];
  for (const seg of segments) {
    const last = merged[merged.length - 1];
    if (seg.kind === 'text' && last && last.kind === 'text') {
      last.value += seg.value;
    } else {
      merged.push({ ...seg });
    }
  }
  return merged;
}

// Build a text expression from a segment list. An empty list collapses to an
// empty text literal; a single text segment returns the literal directly; a
// single expr segment is wrapped in `"" + expr` so the result is text-typed
// for use in instruction/note slots that expect text.
export function buildTextExpression(segments: TextSegment[]): IRExpression {
  const cleaned = mergeAdjacentText(segments).filter((s) => s.kind !== 'text' || s.value !== '');

  if (cleaned.length === 0) {
    return textLiteral('');
  }

  if (cleaned.length === 1) {
    const only = cleaned[0];
    if (only.kind === 'text') return textLiteral(only.value);
    return concat(textLiteral(''), only.expr);
  }

  let acc: IRExpression =
    cleaned[0].kind === 'text' ? textLiteral(cleaned[0].value) : cleaned[0].expr;
  // Ensure the first operand of the resulting binop chain is text-typed so
  // the overall expression's type stays text.
  if (cleaned[0].kind === 'expr') acc = concat(textLiteral(''), acc);
  for (let i = 1; i < cleaned.length; i++) {
    const seg = cleaned[i];
    acc = concat(acc, seg.kind === 'text' ? textLiteral(seg.value) : seg.expr);
  }
  return acc;
}

function textLiteral(value: string): IRExpression {
  return { kind: 'literal', type: 'text', value };
}

function concat(left: IRExpression, right: IRExpression): IRExpression {
  return { kind: 'binop', op: '+', left, right };
}

// Render an expression as a short, human-readable string for display in chips
// and inline editors. Mirrors generateExpression but keeps simple refs and
// arithmetic on a single line without forcing parentheses around the root.
export function renderExpressionInline(expr: IRExpression): string {
  return renderExpr(expr, 0);
}

function renderExpr(expr: IRExpression, parentPrec: number): string {
  switch (expr.kind) {
    case 'literal':
      if (expr.type === 'text') return JSON.stringify(expr.value);
      return String(expr.value);
    case 'param_ref':
    case 'counter_ref':
      return expr.name;
    case 'unop': {
      const inner = renderExpr(expr.operand, 100);
      if (expr.op === 'neg') return `-${inner}`;
      return `${expr.op} ${inner}`;
    }
    case 'binop': {
      const prec = precedence(expr.op);
      const text = `${renderExpr(expr.left, prec)} ${expr.op} ${renderExpr(expr.right, prec + 1)}`;
      return prec < parentPrec ? `(${text})` : text;
    }
  }
}

function precedence(op: string): number {
  switch (op) {
    case 'or':
      return 1;
    case 'and':
      return 2;
    case '==':
    case '!=':
    case '<':
    case '<=':
    case '>':
    case '>=':
      return 3;
    case '+':
    case '-':
      return 4;
    case '*':
    case '/':
    case '%':
      return 5;
    default:
      return 0;
  }
}

// Parse a small expression fragment by wrapping it in a minimal pattern and
// running the full parser, then extracting the row instruction's expression.
// Used by the inline editor for computed segments.
export function parseExpressionFragment(
  source: string,
  parseDSL: (s: string) => IRPattern,
): IRExpression {
  const trimmed = source.trim();
  if (!trimmed) throw new Error('Expression is empty');
  const wrapped = `pattern "p" { segment s { row ${trimmed} } }`;
  const ir = parseDSL(wrapped);
  const row = ir.segments[0]?.body[0];
  if (!row || row.kind !== 'row' || row.instruction.kind !== 'instruction_text') {
    throw new Error('Expression did not parse as a value');
  }
  return row.instruction.expr;
}

// Render a text expression as an interpolated string: text literals are
// emitted as-is and computed subexpressions are wrapped in `{...}`. Only
// descends through `+` chains that actually concatenate text (i.e. contain a
// text literal somewhere in the subtree); pure-arithmetic subexpressions like
// `row + 1` stay together as a single `{row + 1}` placeholder.
export function exprToInterpolatedText(expr: IRExpression): string {
  const parts: string[] = [];
  walkInterpolated(expr, parts);
  return parts.join('');
}

function walkInterpolated(expr: IRExpression, out: string[]) {
  if (
    expr.kind === 'binop' &&
    expr.op === '+' &&
    (containsTextLiteral(expr.left) || containsTextLiteral(expr.right))
  ) {
    walkInterpolated(expr.left, out);
    walkInterpolated(expr.right, out);
    return;
  }
  if (expr.kind === 'literal' && expr.type === 'text') {
    out.push(expr.value as string);
    return;
  }
  out.push(`{${renderExpressionInline(expr)}}`);
}

function containsTextLiteral(expr: IRExpression): boolean {
  if (expr.kind === 'literal' && expr.type === 'text') return true;
  if (expr.kind === 'binop')
    return containsTextLiteral(expr.left) || containsTextLiteral(expr.right);
  if (expr.kind === 'unop') return containsTextLiteral(expr.operand);
  return false;
}

// Parse an interpolated-text input back into a text expression. `{...}`
// runs are parsed as expression fragments; everything else is treated as
// literal text. Throws on unmatched braces or malformed expressions inside
// placeholders. To include a literal "{" in the output text, use the
// placeholder form `{"{"}`.
export function parseInterpolatedText(
  text: string,
  parseDSL: (s: string) => IRPattern,
): IRExpression {
  const segments: TextSegment[] = [];
  let i = 0;
  let textStart = 0;
  while (i < text.length) {
    const ch = text[i];
    if (ch === '{') {
      const close = findMatchingClose(text, i);
      if (close === -1) {
        throw new Error('Unmatched "{" in instruction');
      }
      if (i > textStart) {
        segments.push({ kind: 'text', value: text.slice(textStart, i) });
      }
      const inner = text.slice(i + 1, close).trim();
      if (inner) {
        const expr = parseExpressionFragment(inner, parseDSL);
        segments.push({ kind: 'expr', expr });
      }
      i = close + 1;
      textStart = i;
    } else if (ch === '}') {
      throw new Error('Unmatched "}" in instruction');
    } else {
      i++;
    }
  }
  if (textStart < text.length) {
    segments.push({ kind: 'text', value: text.slice(textStart) });
  }
  return buildTextExpression(segments);
}

function findMatchingClose(text: string, openIdx: number): number {
  let depth = 0;
  for (let i = openIdx; i < text.length; i++) {
    if (text[i] === '{') depth++;
    else if (text[i] === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}
